import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FileSearch,
  Bot,
  BarChart3,
  Radar,
  Workflow,
  ArrowRight,
  Sparkles,
  Check,
  Database,
  PenLine,
  CalendarClock,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/ScrollReveal";
import ScrollCue from "@/components/ScrollCue";
import { scrollToSection } from "@/lib/scrollToSection";
import { subscribeScroll, prefersReducedMotion } from "@/lib/scrollEngine";

type Capability = {
  id: string;
  icon: LucideIcon;
  Visual: (props: { prog: number; active: boolean }) => JSX.Element;
};

const easeOut = (x: number) => 1 - Math.pow(1 - Math.min(Math.max(x, 0), 1), 3);

/* ---------------------------------------------------------------- *
 *  Mini mockups — each animates against its own step progress (0..1)
 * ---------------------------------------------------------------- */

function PdfVisual({ prog }: { prog: number; active: boolean }) {
  const fields = [
    { k: "invoice_no", v: "INV-20294" },
    { k: "total", v: "€ 48,200.00" },
    { k: "due_date", v: "2026-07-14" },
    { k: "vat_id", v: "DE811907980" },
  ];
  // start above the document (off-canvas) and sweep down past the bottom
  const scanY = -25 + easeOut(prog) * 130;
  return (
    <div className="grid grid-cols-2 gap-4 h-full">
      {/* document */}
      <div className="relative flex flex-col h-full rounded-xl border border-border bg-background/70 p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-[10px] text-muted-foreground">invoice_q2.pdf</div>
          <div className="font-mono text-[9px] text-muted-foreground/70 uppercase tracking-wider">invoice</div>
        </div>
        {/* invoice header: from / number */}
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1.5">
            <div className="h-1.5 w-16 rounded-full bg-muted-foreground/40" />
            <div className="h-1 w-12 rounded-full bg-muted-foreground/20" />
            <div className="h-1 w-14 rounded-full bg-muted-foreground/20" />
          </div>
          <div className="space-y-1.5 text-right">
            <div className="ml-auto h-1.5 w-14 rounded-full bg-muted-foreground/40" />
            <div className="ml-auto h-1 w-10 rounded-full bg-muted-foreground/20" />
            <div className="ml-auto h-1 w-12 rounded-full bg-muted-foreground/20" />
          </div>
        </div>
        {/* bill to */}
        <div className="mt-6 space-y-1.5">
          <div className="h-1 w-8 rounded-full bg-muted-foreground/30" />
          <div className="h-1 w-20 rounded-full bg-muted-foreground/20" />
          <div className="h-1 w-16 rounded-full bg-muted-foreground/20" />
        </div>
        {/* line items table */}
        <div className="mt-6 rounded-md border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 bg-muted-foreground/10 px-2 py-1.5">
            <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
            <div className="ml-auto h-1 w-5 rounded-full bg-muted-foreground/30" />
            <div className="h-1 w-7 rounded-full bg-muted-foreground/30" />
          </div>
          {[60, 75, 50, 68, 45].map((w, i) => (
            <div key={i} className="flex items-center gap-2 px-2 py-2 border-t border-border/40">
              <div className="h-1 rounded-full bg-muted-foreground/25" style={{ width: `${w}%` }} />
              <div className="ml-auto h-1 w-5 rounded-full bg-muted-foreground/20" />
              <div className="h-1 w-7 rounded-full bg-muted-foreground/20" />
            </div>
          ))}
        </div>
        {/* totals */}
        <div className="mt-6 flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <div className="h-1 w-8 rounded-full bg-muted-foreground/20" />
            <div className="h-1 w-9 rounded-full bg-muted-foreground/20" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1 w-10 rounded-full bg-muted-foreground/20" />
            <div className="h-1 w-9 rounded-full bg-muted-foreground/20" />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <div className="h-1.5 w-9 rounded-full bg-muted-foreground/40" />
            <div className="h-1.5 w-12 rounded-full bg-accent/50" />
          </div>
        </div>
        {/* footer / payment terms — pinned to bottom */}
        <div className="mt-auto space-y-1.5 pt-6">
          <div className="h-1 w-24 rounded-full bg-muted-foreground/15" />
          <div className="h-1 w-32 rounded-full bg-muted-foreground/15" />
          <div className="h-1 w-20 rounded-full bg-muted-foreground/15" />
        </div>
        {/* scan line */}
        <div
          className="absolute left-0 right-0 h-10 pointer-events-none"
          style={{
            top: `${scanY}%`,
            transform: "translateY(-50%)",
            background:
              "linear-gradient(180deg, transparent, hsl(var(--accent) / 0.28), transparent)",
            boxShadow: "0 0 18px hsl(var(--accent) / 0.45)",
          }}
        />
      </div>
      {/* extracted */}
      <div className="rounded-xl border border-border bg-card/80 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={13} className="text-accent" />
          <span className="font-mono text-[10px] text-accent uppercase tracking-wider">extracted</span>
        </div>
        <div className="space-y-2.5">
          {fields.map((f, i) => {
            const on = prog > 0.2 + i * 0.16;
            return (
              <div
                key={f.k}
                className="flex items-center justify-between gap-2 rounded-md border border-border/60 bg-background/60 px-2.5 py-1.5 transition-all duration-300"
                style={{ opacity: on ? 1 : 0.18, transform: on ? "none" : "translateY(4px)" }}
              >
                <span className="font-mono text-[10px] text-muted-foreground truncate">{f.k}</span>
                <span className="font-mono text-[10px] text-foreground whitespace-nowrap shrink-0">{on ? f.v : "···"}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const clamp01 = (x: number) => Math.min(Math.max(x, 0), 1);

/* Free-floating animated activity — no boxes, no bars, no labels.
   Just the work itself, driven by local progress (0..1). */
function WorkScene({ index, local }: { index: number; local: number }) {
  // 0 — Reading CRM records: a beam sweeps down floating record lines
  if (index === 0) {
    const rows = 7;
    const scan = local * (rows + 1.5);
    return (
      <div className="relative flex flex-col justify-center gap-3.5">
        {Array.from({ length: rows }).map((_, i) => {
          const read = scan > i + 1;
          const isScanning = scan > i && scan <= i + 1;
          return (
            <div key={i} className="flex items-center gap-3">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full transition-all duration-200"
                style={{
                  background: read || isScanning ? "hsl(var(--accent))" : "hsl(var(--muted-foreground) / 0.3)",
                  boxShadow: isScanning ? "0 0 14px hsl(var(--accent))" : "none",
                  transform: isScanning ? "scale(1.4)" : "scale(1)",
                }}
              />
              <span
                className="h-[3px] rounded-full transition-all duration-300"
                style={{
                  width: `${42 + ((i * 13) % 45)}%`,
                  background: read || isScanning ? "hsl(var(--accent) / 0.7)" : "hsl(var(--muted-foreground) / 0.2)",
                  opacity: read || isScanning ? 1 : 0.5,
                }}
              />
            </div>
          );
        })}
      </div>
    );
  }

  // 1 — Analyzing churn cohorts: a line chart draws itself across floating points
  if (index === 1) {
    const pts = [78, 60, 68, 45, 52, 33, 40, 22];
    const W = 220;
    const H = 120;
    const step = W / (pts.length - 1);
    const coords = pts.map((y, i) => [i * step, (y / 100) * H]);
    const drawn = Math.max(1, Math.round(local * (pts.length - 1)));
    const path = coords
      .slice(0, drawn + 1)
      .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`)
      .join(" ");
    return (
      <div className="flex items-center justify-center">
        <svg viewBox={`0 0 ${W} ${H + 8}`} className="w-full overflow-visible">
          <path
            d={path}
            fill="none"
            stroke="hsl(var(--accent))"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ filter: "drop-shadow(0 0 6px hsl(var(--accent) / 0.6))" }}
          />
          {coords.map(([x, y], i) => {
            const on = i <= drawn;
            return (
              <circle
                key={i}
                cx={x}
                cy={y}
                r={i === drawn ? 5 : 3.5}
                fill={on ? "hsl(var(--accent))" : "hsl(var(--muted-foreground) / 0.3)"}
                style={{ transition: "all 200ms", filter: i === drawn ? "drop-shadow(0 0 8px hsl(var(--accent)))" : "none" }}
              />
            );
          })}
        </svg>
      </div>
    );
  }

  // 2 — Drafting 3 variants: floating text lines type themselves out
  if (index === 2) {
    return (
      <div className="flex flex-col justify-center gap-5">
        {[0, 1, 2].map((v) => {
          const start = v * 0.26;
          const vLocal = clamp01((local - start) / 0.4);
          const lineWidths = [100, 88, 64];
          return (
            <div key={v} className="space-y-2">
              {lineWidths.map((mw, l) => {
                const w = clamp01((vLocal - l * 0.28) * 3.2);
                const typing = w > 0 && w < 1;
                return (
                  <div key={l} className="flex items-center">
                    <span
                      className="h-[3px] rounded-full bg-accent/70 transition-[width] duration-150"
                      style={{ width: `${w * mw}%` }}
                    />
                    {typing && <span className="ml-0.5 h-3 w-[2px] bg-accent animate-pulse" />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }

  // 3 — Preparing schedule: floating week grid fills, two slots lock in
  const cols = 7;
  const gridRows = 4;
  const target = Math.floor(local * cols * gridRows);
  return (
    <div className="grid grid-cols-7 gap-2.5">
      {Array.from({ length: cols * gridRows }).map((_, i) => {
        const filled = i <= target;
        const isCurrent = i === target;
        const picked = local > 0.6 && (i === 9 || i === 18);
        return (
          <div
            key={i}
            className="aspect-square rounded-[3px] transition-all duration-200"
            style={{
              background: picked
                ? "hsl(var(--accent))"
                : filled
                ? "hsl(var(--accent) / 0.25)"
                : "hsl(var(--muted-foreground) / 0.12)",
              boxShadow: isCurrent || picked ? "0 0 12px hsl(var(--accent) / 0.6)" : "none",
              transform: picked ? "scale(1.12)" : "scale(1)",
            }}
          />
        );
      })}
    </div>
  );
}




function ChatVisual({ prog }: { prog: number; active: boolean }) {
  const msgs = [
    { role: "user", text: "Summarise last month's churn." },
    { role: "bot", text: "Churn fell to 2.1% (-0.6pp). Main driver: onboarding fixes." },
    { role: "user", text: "Draft a retention email." },
    { role: "bot", text: "Done — 3 variants ready, tone: friendly. Want me to schedule it?" },
  ];

  // background work the copilot performs, synced to scroll progress.
  // Each task gets its own animated "scene" instead of a static bar.
  const tasks = [
    { icon: Database, label: "Reading CRM records", from: 0.0, span: 0.25 },
    { icon: BarChart3, label: "Analyzing churn cohorts", from: 0.25, span: 0.25 },
    { icon: PenLine, label: "Drafting 3 variants", from: 0.5, span: 0.25 },
    { icon: CalendarClock, label: "Preparing schedule", from: 0.75, span: 0.25 },
  ];
  const found = tasks.findIndex((t) => prog < t.from + t.span);
  const safeIdx = found < 0 ? tasks.length - 1 : found;
  const local = clamp01((prog - tasks[safeIdx].from) / tasks[safeIdx].span);


  return (
    <div className="grid h-full grid-cols-2 gap-4">
      {/* LEFT — just the activity, free-floating, animated */}
      <div className="relative flex items-center justify-center">
        <div key={safeIdx} className="w-full animate-fade-in">
          <WorkScene index={safeIdx} local={local} />
        </div>
      </div>


      {/* RIGHT — chat window, 50% width */}
      <div className="flex h-full flex-col rounded-xl border border-border bg-background/70 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-card/60">
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/20">
            <Bot size={13} className="text-accent" />
          </span>
          <span className="font-mono text-[12px] text-foreground">copilot</span>
          <span className="ml-auto h-2 w-2 rounded-full bg-success animate-pulse" />
        </div>
        <div className="flex-1 flex flex-col justify-end gap-1.5 md:gap-3 p-3 md:p-4 overflow-hidden">
          {msgs.map((m, i) => {
            const on = prog > i * 0.22;
            if (!on) return null;
            const isUser = m.role === "user";
            return (
              <div
                key={i}
                className={`flex animate-fade-in ${isUser ? "justify-end" : "justify-start"}`}
              >
                <span
                  className={`max-w-[88%] rounded-2xl px-2.5 py-1.5 md:px-3.5 md:py-2 text-[11px] leading-snug md:text-[13px] md:leading-relaxed ${
                    isUser
                      ? "bg-primary/25 text-foreground rounded-br-sm"
                      : "bg-card border border-border text-muted-foreground rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </span>
              </div>
            );
          })}
          {prog > 0.9 && (
            <div className="flex justify-start gap-1 pl-1">
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse [animation-delay:300ms]" />
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function KpiVisual({ prog }: { prog: number; active: boolean }) {
  const bars = [42, 58, 47, 71, 63, 85, 78, 96];
  const stats = [
    { l: "MRR", v: "€128k", d: "+14%" },
    { l: "Active", v: "8,412", d: "+6%" },
    { l: "Churn", v: "2.1%", d: "-0.6pp" },
  ];
  return (
    <div className="flex h-full flex-col gap-3">
      <div className="grid grid-cols-3 gap-3">
        {stats.map((s, i) => {
          const on = prog > 0.1 + i * 0.12;
          return (
            <div
              key={s.l}
              className="rounded-lg border border-border bg-card/80 p-2.5 transition-all duration-300"
              style={{ opacity: on ? 1 : 0.2 }}
            >
              <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">{s.l}</div>
              <div className="mt-0.5 text-base font-bold text-foreground">{s.v}</div>
              <div className="font-mono text-[9px] text-accent">{s.d}</div>
            </div>
          );
        })}
      </div>
      <div className="relative flex-1 rounded-xl border border-border bg-background/70 p-4">
        <span className="font-mono text-[10px] text-muted-foreground">revenue / week</span>
        <div className="mt-3 flex h-[calc(100%-1.5rem)] items-end gap-2">
          {bars.map((h, i) => {
            const local = easeOut((prog - i * 0.05) * 1.6);
            return (
              <div key={i} className="flex-1 rounded-t-sm bg-gradient-to-t from-primary to-accent" style={{ height: `${h * local}%` }} />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ScraperVisual({ prog }: { prog: number; active: boolean }) {
  const rows = [
    ["acme.io", "€2.4M", "Series A"],
    ["nova.dev", "€880k", "Seed"],
    ["flux.app", "€5.1M", "Series B"],
    ["orbit.co", "€1.2M", "Seed"],
  ];
  return (
    <div className="grid grid-cols-5 gap-4 h-full">
      {/* sources */}
      <div className="col-span-2 flex flex-col justify-center gap-2.5">
        <span className="font-mono text-[10px] text-muted-foreground">sources</span>
        {["webpage", "api/json", "sitemap", "pdf"].map((s, i) => {
          const on = prog > i * 0.12;
          return (
            <div
              key={s}
              className="flex items-center gap-2 rounded-md border border-border bg-card/70 px-2.5 py-1.5 transition-all duration-300"
              style={{ opacity: on ? 1 : 0.25, transform: on ? "none" : "translateX(-6px)" }}
            >
              <Radar size={12} className="text-accent shrink-0" />
              <span className="font-mono text-[10px] text-muted-foreground truncate">{s}</span>
            </div>
          );
        })}
      </div>
      {/* structured table */}
      <div className="col-span-3 rounded-xl border border-border bg-background/70 p-3 overflow-hidden">
        <div className="mb-2 grid grid-cols-3 gap-2 border-b border-border pb-1.5 font-mono text-[9px] uppercase tracking-wider text-accent">
          <span>domain</span><span>funding</span><span>stage</span>
        </div>
        <div className="space-y-1.5">
          {rows.map((r, i) => {
            const on = prog > 0.25 + i * 0.16;
            return (
              <div
                key={i}
                className="grid grid-cols-3 gap-2 rounded-md bg-card/50 px-1.5 py-1.5 transition-all duration-300"
                style={{ opacity: on ? 1 : 0, transform: on ? "none" : "translateY(6px)" }}
              >
                {r.map((c, j) => (
                  <span key={j} className="font-mono text-[10px] text-foreground truncate">{c}</span>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function OrgVisual({ prog }: { prog: number; active: boolean }) {
  const buckets = [
    { label: "Sales", items: ["Lead · acme", "Quote #204"] },
    { label: "Support", items: ["Ticket #88", "Bug report"] },
    { label: "Finance", items: ["Invoice Q2", "Payroll"] },
  ];
  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card/70 px-3 py-2">
        <Workflow size={13} className="text-accent" />
        <span className="font-mono text-[10px] text-muted-foreground">142 unsorted items</span>
        <span className="ml-auto font-mono text-[10px] text-accent">auto-routing…</span>
      </div>
      <div className="grid flex-1 grid-cols-3 gap-3">
        {buckets.map((b, bi) => (
          <div key={b.label} className="flex flex-col rounded-xl border border-border bg-background/70 p-2.5">
            <span className="mb-2 font-mono text-[10px] uppercase tracking-wider text-foreground">{b.label}</span>
            <div className="space-y-1.5">
              {b.items.map((it, ii) => {
                const on = prog > 0.15 + (bi * 0.1 + ii * 0.18);
                return (
                  <div
                    key={it}
                    className="flex items-center gap-1.5 rounded-md border border-border/60 bg-card/60 px-2 py-1.5 transition-all duration-300"
                    style={{ opacity: on ? 1 : 0, transform: on ? "none" : "scale(0.92)" }}
                  >
                    <Check size={10} className="text-accent shrink-0" />
                    <span className="font-mono text-[9px] text-muted-foreground truncate">{it}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const capabilities: Capability[] = [
  { id: "pdf", icon: FileSearch, Visual: PdfVisual },
  { id: "copilot", icon: Bot, Visual: ChatVisual },
  { id: "kpi", icon: BarChart3, Visual: KpiVisual },
  { id: "scraper", icon: Radar, Visual: ScraperVisual },
  { id: "org", icon: Workflow, Visual: OrgVisual },
];

/* ---------------------------------------------------------------- */

const Heading = () => {
  const { t } = useTranslation();
  return (
    <div className="max-w-3xl">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-3">
        {t("aiEmbed.headingLine1")}{" "}
        <span className="gradient-text">{t("aiEmbed.headingLine2")}</span>
      </h2>
      <p className="text-sm md:text-base text-muted-foreground">{t("aiEmbed.subtitle")}</p>
    </div>
  );
};

const isPrerender = () =>
  typeof window !== "undefined" &&
  (window as unknown as { __PRERENDER__?: boolean }).__PRERENDER__ === true;

/** Mobile card whose visual builds up as it scrolls through the viewport. */
const MobileVisualCard = ({
  Visual,
}: {
  Visual: (props: { prog: number; active: boolean }) => JSX.Element;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [prog, setProg] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion() || isPrerender()) {
      setProg(1);
      return;
    }
    return subscribeScroll(() => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the card top reaches 85% of viewport, 1 when it reaches 35%
      const start = vh * 0.85;
      const end = vh * 0.35;
      const raw = (start - rect.top) / (start - end);
      setProg(Math.min(Math.max(raw, 0), 1));
    });
  }, []);

  return (
    <div ref={ref} className="h-64 border-t border-border bg-background/40 p-4">
      <Visual prog={prog} active={prog > 0.02} />
    </div>
  );
};

const AIEmbedSection = () => {
  const { t } = useTranslation();
  const items = t("aiEmbed.items", { returnObjects: true }) as { title: string; desc: string }[];
  const outerRef = useRef<HTMLDivElement>(null);
  const [p, setP] = useState(0);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    if (prefersReducedMotion() || isPrerender()) {
      setP(0.06);
      return;
    }
    return subscribeScroll(() => {
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      setP(total > 0 ? scrolled / total : 0);
    });
  }, []);

  const n = capabilities.length;
  // map progress to a step + intra-step progress, with small dead-zones at edges
  const raw = Math.min(Math.max((p - 0.04) / 0.92, 0), 0.9999) * n;
  const activeIndex = Math.min(n - 1, Math.floor(raw));
  const stepProg = raw - activeIndex;

  return (
    <>
      {/* DESKTOP — pinned scroll-driven capability showcase */}
      <section
        ref={outerRef}
        id="ai-embed"
        className="relative hidden md:block bg-background text-foreground border-b border-border"
        style={{ height: "1000vh" }}
      >
        <div className="sticky top-0 h-[100dvh] flex flex-col justify-center overflow-hidden pt-24">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="aurora-blob aurora-2 w-[45%] h-[45%] top-[10%] right-[-6%]" />
            <div className="aurora-blob aurora-3 w-[40%] h-[40%] bottom-[-10%] left-[-4%]" />
          </div>
          <div className="absolute inset-0 tech-grid opacity-[0.08] pointer-events-none" />

          <div className="container relative z-10 flex-1 min-h-0 flex flex-col justify-center py-4">
            <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-8 lg:gap-12 items-center">
              {/* LEFT — heading + stepper list + CTAs */}
              <div className="flex flex-col gap-7">
                <Heading />
                <div className="relative pl-1">
                {/* progress rail — runs through the icon node centres */}
                <div className="absolute left-[19px] top-5 bottom-5 w-px bg-border">
                  <div
                    className="absolute left-0 top-0 w-px bg-accent"
                    style={{ height: `${((activeIndex + stepProg) / n) * 100}%`, transition: "height 120ms linear" }}
                  />
                </div>
                <ul className="space-y-1">
                  {capabilities.map((c, i) => {
                    const active = i === activeIndex;
                    const done = i < activeIndex;
                    const Icon = c.icon;
                    return (
                      <li key={c.id}>
                        <div className="flex items-start gap-4 py-2.5">
                          {/* node sits on top of the rail */}
                          <span
                            className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-all duration-300"
                            style={{
                              borderColor: active || done ? "hsl(var(--accent) / 0.6)" : "hsl(var(--border))",
                               background: active
                                ? "linear-gradient(hsl(var(--accent) / 0.15), hsl(var(--accent) / 0.15)), hsl(var(--background))"
                                : "hsl(var(--background))",
                              boxShadow: active ? "0 0 0 4px hsl(var(--accent) / 0.08)" : "none",
                            }}
                          >
                            <Icon
                              size={16}
                              className="transition-colors duration-300"
                              style={{ color: active || done ? "hsl(var(--accent))" : "hsl(var(--muted-foreground))" }}
                            />
                          </span>
                          <div className="min-w-0 pt-1">
                            <h3
                              className="text-sm font-semibold leading-tight transition-colors duration-300"
                              style={{ color: active ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground) / 0.7)" }}
                            >
                              {items?.[i]?.title}
                            </h3>
                            <p
                              className="text-xs text-muted-foreground transition-opacity duration-300"
                              style={{ opacity: active ? 1 : 0, marginTop: 4 }}
                            >
                              {items?.[i]?.desc}
                            </p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                </div>

                {/* CTAs — inside the section, under the stations */}
                <div className="flex flex-col sm:flex-row gap-3 pl-1">
                  <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
                    <a
                      href="#blueprint"
                      onClick={(e) => { e.preventDefault(); scrollToSection("blueprint"); }}
                      className="inline-flex w-full sm:w-auto justify-center items-center gap-2.5 px-6 py-3 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium transition-transform duration-200 active:scale-[0.97] group"
                    >
                      <Sparkles size={16} />
                      {t("aiEmbed.ctaPrimary")}
                      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  </span>
                  <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
                    <Link
                      to="/contact"
                      className="inline-flex w-full sm:w-auto justify-center items-center gap-2.5 px-6 py-3 btn-clip bg-card/40 text-foreground font-medium transition-transform duration-200 active:scale-[0.97] group"
                    >
                      {t("aiEmbed.ctaSecondary")}
                      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </span>
                </div>
              </div>

              {/* RIGHT — free-floating content (no browser chrome, static) */}
              <div className="relative aspect-[4/3] w-full">
                {capabilities.map((c, i) => {
                  const active = i === activeIndex;
                  const Visual = c.Visual;
                  return (
                    <div
                      key={c.id}
                      className="absolute inset-0 transition-opacity duration-300"
                      style={{ opacity: active ? 1 : 0, pointerEvents: active ? "auto" : "none" }}
                      aria-hidden={!active}
                    >
                      <Visual prog={active ? stepProg : 0} active={active} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <ScrollCue progress={(activeIndex + stepProg) / n} />
        </div>
      </section>

      {/* MOBILE — stacked cards */}
      <section className="md:hidden relative bg-background text-foreground border-b border-border py-16 overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-[0.06] pointer-events-none" />
        <div className="container relative z-10">
          <div className="mb-10">
            <Heading />
          </div>
          <div className="space-y-4">
            {capabilities.map((c, i) => {
              const Icon = c.icon;
              const Visual = c.Visual;
              return (
                <ScrollReveal key={c.id} delay={i * 60}>
                  <div className="rounded-2xl border border-border bg-card/50 overflow-hidden">
                    <div className="flex items-start gap-3 p-4">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-accent/40 bg-accent/10">
                        <Icon size={16} className="text-accent" />
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">{items?.[i]?.title}</h3>
                        <p className="mt-1 text-xs text-muted-foreground">{items?.[i]?.desc}</p>
                      </div>
                    </div>
                    <MobileVisualCard Visual={Visual} />
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA — mobile only (desktop CTAs live inside the showcase section) */}
      <section className="md:hidden py-16 bg-background text-foreground border-b border-border relative overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-[0.06] pointer-events-none" />
        <div className="container relative z-10">
          <ScrollReveal className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
            <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
              <a
                href="#blueprint"
                onClick={(e) => { e.preventDefault(); scrollToSection("blueprint"); }}
                className="inline-flex w-full sm:w-auto justify-center items-center gap-3 px-8 py-4 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium text-lg transition-transform duration-200 active:scale-[0.97] group"
              >
                <Sparkles size={18} />
                {t("aiEmbed.ctaPrimary")}
                <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </span>
            <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
              <Link
                to="/contact"
                className="inline-flex w-full sm:w-auto justify-center items-center gap-3 px-8 py-4 btn-clip bg-card/40 text-foreground font-medium text-lg transition-transform duration-200 active:scale-[0.97] group"
              >
                {t("aiEmbed.ctaSecondary")}
                <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </span>
          </ScrollReveal>
        </div>
      </section>
    </>
  );
};

export default AIEmbedSection;
